'use client';

import type { CSSProperties } from 'react';

import styles from './Toggle.module.sass';

export type ToggleOption<T extends string> = {
  value: T;
  label: React.ReactNode;
};

type Props<T extends string> = {
  options: ToggleOption<T>[];
  value: T | undefined;
  onChange: (value: T) => void;
  label?: string;
  className?: string;
};

export default function Toggle<T extends string>({
  options,
  value,
  onChange,
  label,
  className,
}: Props<T>) {
  const activeIndex = Math.max(
    0,
    options.findIndex((o) => o.value === value),
  );
  const cycle = () => {
    if (options.length === 0) return;
    const next = options[(activeIndex + 1) % options.length];
    onChange(next.value);
  };

  return (
    <div className={`${styles.wrapper} ${className ?? ''}`}>
      {label && <span className={styles.label}>{label}</span>}
      <button
        type="button"
        className={styles.toggle}
        onClick={cycle}
        style={
          {
            '--toggle-count': options.length,
            '--toggle-index': activeIndex,
          } as CSSProperties
        }
      >
        <span className={styles.indicator} aria-hidden />
        {options.map((opt) => (
          <span key={opt.value} className={styles.option} data-active={opt.value === value}>
            {opt.label}
          </span>
        ))}
      </button>
    </div>
  );
}
